import { getResolvedRef } from '@scalar/workspace-store/helpers/get-resolved-ref';
import { heading, inlineCode, item, list, paragraph, strong, text } from './markdown-nodes.js';
const flowNames = {
    implicit: 'implicit',
    password: 'password',
    clientCredentials: 'client credentials',
    authorizationCode: 'authorization code',
    deviceAuthorization: 'device authorization',
};
/** Summarize a security scheme on one line, for example "API key in header `X-Api-Key`". */
const summarizeScheme = (scheme) => {
    const value = scheme;
    switch (value.type) {
        case 'apiKey':
            return [text(`API key in ${String(value.in ?? 'header')} `), inlineCode(value.name)];
        case 'http': {
            const name = typeof value.scheme === 'string' ? value.scheme.toLowerCase() : '';
            return [
                text(`HTTP ${name || 'authentication'}`),
                ...(typeof value.bearerFormat === 'string' ? [text(' ('), inlineCode(value.bearerFormat), text(')')] : []),
            ];
        }
        case 'oauth2': {
            const flows = Object.entries(typeof value.flows === 'object' && value.flows ? value.flows : {});
            const nodes = [text('OAuth 2.0')];
            for (const [index, [flow, settings]] of flows.entries()) {
                const urls = settings;
                nodes.push(text(`${index ? '; ' : ': '}${flowNames[flow] ?? flow}`));
                for (const [key, label] of [
                    ['authorizationUrl', 'authorize'],
                    ['deviceAuthorizationUrl', 'device authorization'],
                    ['tokenUrl', 'token'],
                    ['refreshUrl', 'refresh'],
                ]) {
                    // Coercion fills in optional URLs as empty strings.
                    if (typeof urls[key] === 'string' && urls[key])
                        nodes.push(text(`, ${label} `), inlineCode(urls[key]));
                }
            }
            return nodes;
        }
        case 'openIdConnect':
            return [text('OpenID Connect '), inlineCode(value.openIdConnectUrl)];
        case 'mutualTLS':
            return [text('Mutual TLS')];
        default:
            return [inlineCode(value.type)];
    }
};
/**
 * Preserve OR between requirements and AND between schemes within a requirement.
 * Without declared requirements there is nothing to say: only an explicit empty list means that
 * no authentication is required.
 */
export const renderSecurity = async (requirements, schemes, description, level = 4) => {
    if (!requirements)
        return [];
    const nodes = [heading(level, text('Authentication'))];
    if (!requirements.length)
        nodes.push(paragraph(text('No authentication required.')));
    for (const [index, requirement] of requirements.entries()) {
        if (index)
            nodes.push(paragraph(text('Or:')));
        const entries = Object.entries(requirement);
        if (!entries.length) {
            nodes.push(paragraph(text('No authentication required.')));
            continue;
        }
        const entriesNodes = [];
        for (const [name, scopes] of entries) {
            const scheme = getResolvedRef(schemes?.[name]);
            const line = [strong(text(name))];
            if (scheme)
                line.push(text(': '), ...summarizeScheme(scheme));
            if (scopes?.length)
                line.push(text(', scopes: '), inlineCode(scopes.join(', ')));
            entriesNodes.push(item(paragraph(...line), ...(await description(scheme?.description))));
        }
        nodes.push(list(entriesNodes));
    }
    return nodes;
};
